// editor-preview-dialog.tsx — the ONE dialog frame for "an editor with the
// WhatsApp preview beside it".
//
// The schedule-message editor and the campaign message editors used to open two
// different ways: this Radix Dialog for the former, a hand-rolled fixed overlay
// at z-index 2147483000 for the latter. Every nested dialog the shared message
// editor opens (the Templates picker, Save as template) is a Radix Dialog at
// ~20000, so inside the campaign overlay they opened BEHIND it. Hosting both
// editors in the same Dialog makes the nested ones stack by Radix's own layer
// order — identically in both — instead of each host fighting it with z-index.
import { BTPaperProps } from '@common-components/atoms/bt-paper/bt-paper';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useIsMobile } from '@common/hooks/use-is-mobile';
import { Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import PreviewScheduleMessage from './preview-schedule-message';
import { COMPOSE_SHORT_HEIGHT_QUERY } from './compose-layout';
import composeStyles from './compose-dialog.module.scss';

interface EditorPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Screen-reader title (the visible title belongs to the editor card). */
  title: string;
  /** Show the WhatsApp preview column beside the editor (desktop only). */
  showPreview?: boolean;
  /** The Dialog's own corner X. The campaign cards carry their own X in their
   *  header, which guards a close while a save is in flight. */
  showCloseButton?: boolean;
  maxWidth?: number;
  /** The editor card. Rendered only while open — the editors are heavy. */
  children: React.ReactNode;
}

const EditorPreviewDialog: React.FC<EditorPreviewDialogProps> = ({
  open,
  onClose,
  title,
  showPreview = true,
  showCloseButton = true,
  maxWidth = 1250,
  children,
}) => {
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  const dialogDirection = i18n.dir(i18n.language) === 'rtl' ? 'rtl' : 'ltr';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        showCloseButton={showCloseButton}
        // The inline maxWidth below is what sizes it; the class is the fallback.
        className={isMobile ? "p-0 bt-editor-mobile" : `max-w-[1150px] w-full p-0 ${composeStyles.editorDialogShort}`}
        style={isMobile ? {
          // Full-screen sheet on phones: use the whole viewport, contain any
          // horizontal spill (no more left/right clip), and let the editor own
          // its internal vertical scroll + sticky footer.
          direction: dialogDirection,
          backgroundColor: 'var(--color-surface)',
          overflow: 'hidden',
          maxWidth: '100vw',
          width: '100vw',
          maxHeight: '100dvh',
          height: '100dvh',
          border: 'none',
          borderRadius: 0,
        } : {
          direction: dialogDirection,
          backgroundColor: 'transparent',
          overflow: 'visible',
          maxWidth: `${maxWidth}px`,
          width: '100%',
          border: 'none',
          ...BTPaperProps,
        }}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {open && (
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: isMobile ? 0 : 2,
            height: isMobile ? '100dvh' : '90vh',
            minHeight: 0,
            // Short laptop screens (13-14"): 90vh plus DialogContent's 24px
            // padding left ~65px of dead backdrop on a ~645px viewport — hand it
            // to the editor instead. 32px = DialogContent's 1rem max-h margin +
            // its (short-screen) 8px padding top and bottom.
            ...(!isMobile && {
              [`@media ${COMPOSE_SHORT_HEIGHT_QUERY}`]: { height: 'calc(100dvh - 32px)' },
            }),
            // NOT `row-reverse` in RTL: DialogContent already carries
            // `direction: rtl`, so a plain `row` lays its children out
            // right-to-left on its own. Reversing on top of that double-flips
            // back to LTR order and left the WhatsApp preview pinned to the
            // right in both directions while the rest of the dialog mirrored.
            flexDirection: { xs: 'column', md: 'row' }
          }}>
            {children}

            {showPreview && (
              <Box sx={{ width: "30%", minWidth: "350px", display: { xs: 'none', md: 'block' } }}>
                <PreviewScheduleMessage />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditorPreviewDialog;
